import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { accountScopedStorage, rehydrateOnAccountChange } from '../services/accountScopedPersist';

/**
 * Client TLS certificates are CREDENTIALS — the cert/key pair is what proves,
 * to a server, that the request comes from this person. They are therefore
 * per-account (`apiboo-<accountId>-certificates`), not device-global: another
 * account signing in on the same machine must not be able to enumerate, use,
 * or delete them. The pre-namespace `apiboo-certificates` key is deleted once
 * by purgeLegacyStorageKeys() and never read again.
 *
 * CA certificates ride along in the same key. They are not secret, but they
 * are a trust decision (which roots this user accepts), and `verifySSL` is a
 * security setting — neither should be inherited from whoever used the machine
 * before.
 */
const EMPTY_PERSISTED = { clientCerts: [], caCerts: [], verifySSL: true };
const certificateStorage = accountScopedStorage('certificates');

export const useCertificateStore = create(
  persist(
    (set) => ({
      // [{ id, domain, type, certPath, keyPath, label }]
      // NOTE: passphrase is intentionally NOT stored here — kept in session memory only.
      clientCerts: [],
      // [{ id, path, label }]
      caCerts: [],
      verifySSL: true,

      addClientCert: (cert) =>
        set((s) => ({ clientCerts: [...s.clientCerts, cert] })),

      removeClientCert: (id) =>
        set((s) => ({ clientCerts: s.clientCerts.filter((c) => c.id !== id) })),

      updateClientCert: (id, updates) =>
        set((s) => ({
          clientCerts: s.clientCerts.map((c) => (c.id === id ? { ...c, ...updates } : c)),
        })),

      addCACert: (ca) =>
        set((s) => ({ caCerts: [...s.caCerts, ca] })),

      removeCACert: (id) =>
        set((s) => ({ caCerts: s.caCerts.filter((c) => c.id !== id) })),

      setVerifySSL: (v) => set({ verifySSL: v }),
    }),
    {
      // `name` is unused by the storage adapter below — the real key is
      // `apiboo-<accountId>-certificates`, rebuilt on every read/write.
      name: 'certificates',
      storage: createJSONStorage(() => certificateStorage),
      // Explicitly list what gets persisted — passphrases are session-only and never stored.
      partialize: (state) => ({
        clientCerts: state.clientCerts,
        caCerts: state.caCerts,
        verifySSL: state.verifySSL,
      }),
    }
  )
);

// An account switch must not leave the outgoing account's certificates in
// memory: reset to defaults, then re-read under the incoming account.
rehydrateOnAccountChange(useCertificateStore, EMPTY_PERSISTED, certificateStorage);
